class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    if (!this.mainDetailsToggle) return;

    const summary = this.mainDetailsToggle.querySelector('summary');
    if (!summary) return;

    this.content = summary.nextElementSibling;
    this.onFocusOutHandler = this.onFocusOut.bind(this);
    this.onToggleHandler = this.onToggle.bind(this);

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOutHandler);
    this.mainDetailsToggle.addEventListener('toggle', this.onToggleHandler);
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.content || !this.content.getAnimations) return;
    if (this.closest('header-menu')) return;
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  open() {
    if (!this.mainDetailsToggle || this.mainDetailsToggle.hasAttribute('open')) return;

    this.mainDetailsToggle.setAttribute('open', '');
    const summary = this.mainDetailsToggle.querySelector('summary');
    summary?.setAttribute('aria-expanded', 'true');
    this.mainDetailsToggle.dispatchEvent(new Event('toggle', { bubbles: true }));
  }

  close() {
    if (!this.mainDetailsToggle || !this.mainDetailsToggle.hasAttribute('open')) return;

    this.mainDetailsToggle.removeAttribute('open');
    const summary = this.mainDetailsToggle.querySelector('summary');
    summary?.setAttribute('aria-expanded', 'false');
    this.mainDetailsToggle.dispatchEvent(new Event('toggle', { bubbles: true }));
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  static MENU_SWITCH_MS = 200;
  static CLOSE_ANIMATION_MS = 320;

  static closeSiblingMenus(currentMenu) {
    document.querySelectorAll('header-menu').forEach((menu) => {
      if (menu === currentMenu) return;
      window.clearTimeout(menu.closeTimeout);
      window.clearTimeout(menu.openTimeout);
      menu.closeTimeout = null;
      menu.openTimeout = null;
      menu._hoverActive = false;
      menu.close(true);
    });
    HeaderMenu.updateBodyScrollLock();
  }

  static updateBodyScrollLock() {
    if (!window.matchMedia('(min-width: 990px)').matches) {
      document.documentElement.classList.remove('header-menu-open');
      return;
    }

    const hasOpenMenu = Array.from(document.querySelectorAll('header-menu')).some((menu) =>
      menu.mainDetailsToggle?.hasAttribute('open')
    );
    document.documentElement.classList.toggle('header-menu-open', hasOpenMenu);
  }

  constructor() {
    super();
    if (!this.mainDetailsToggle) return;

    this.header = document.querySelector('.header-wrapper');
    this.summary = this.mainDetailsToggle.querySelector('summary');
    if (!this.summary) return;

    this.hoverMedia = window.matchMedia('(min-width: 990px)');
    this.pendingClose = false;
    this.onCloseTransitionEnd = this.onCloseTransitionEnd.bind(this);

    this.onHoverMediaChange = this.onHoverMediaChange.bind(this);
    this.hoverMedia.addEventListener('change', this.onHoverMediaChange);
    this.onHoverMediaChange(this.hoverMedia);
  }

  disconnectedCallback() {
    this.hoverMedia.removeEventListener('change', this.onHoverMediaChange);
    this.abortCloseAnimation();
    this.unbindHoverEvents();
  }

  resetDropdownInlineStyles() {
    if (!this.menuContent) return;
    this.menuContent.style.transition = '';
    this.menuContent.style.opacity = '';
    this.menuContent.style.maxHeight = '';
  }

  abortCloseAnimation() {
    this.pendingClose = false;
    window.clearTimeout(this.closeFallbackTimeout);
    this.closeFallbackTimeout = null;

    if (this.menuContent) {
      this.menuContent.removeEventListener('transitionend', this.onCloseTransitionEnd);
      this.resetDropdownInlineStyles();
      if (this.mainDetailsToggle?.hasAttribute('open')) {
        this.menuContent.classList.remove('is-menu-closing');
      }
    }
  }

  onFocusOut() {
    if (this.hoverMedia?.matches) return;

    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  open() {
    this.abortCloseAnimation();

    if (!this.mainDetailsToggle || this.mainDetailsToggle.hasAttribute('open')) {
      this.resetDropdownInlineStyles();
      this.menuContent?.classList.remove('is-menu-closing');
      return;
    }

    super.open();
    this.resetDropdownInlineStyles();
    this.menuContent?.classList.remove('is-menu-closing');
    HeaderMenu.updateBodyScrollLock();
  }

  closeDropdown() {
    const panel = this.menuContent;
    panel.getAnimations?.().forEach((animation) => animation.cancel());
    super.close();
    this.resetDropdownInlineStyles();
    HeaderMenu.updateBodyScrollLock();
  }

  close(force = false) {
    if (!this.mainDetailsToggle?.hasAttribute('open')) return;

    if (this.isDropdownMenu && !force && this.hoverMedia.matches && this.menuContent) {
      this.closeDropdown();
      return;
    }

    if (this.pendingClose) return;

    if (force || !this.hoverMedia.matches || !this.menuContent) {
      this.abortCloseAnimation();
      super.close();
      this.menuContent?.classList.remove('is-menu-closing');
      HeaderMenu.updateBodyScrollLock();
      return;
    }

    if (this.menuContent.classList.contains('is-menu-closing')) return;

    this.pendingClose = true;
    this.menuContent.getAnimations?.().forEach((animation) => animation.cancel());
    this.menuContent.classList.add('is-menu-closing');
    void this.menuContent.offsetHeight;
    this.menuContent.addEventListener('transitionend', this.onCloseTransitionEnd);

    window.clearTimeout(this.closeFallbackTimeout);
    this.closeFallbackTimeout = window.setTimeout(() => {
      this.finishCloseAnimation();
    }, HeaderMenu.CLOSE_ANIMATION_MS + 50);
  }

  onCloseTransitionEnd(event) {
    if (!this.menuContent || event.target !== this.menuContent) return;
    if (event.propertyName !== 'opacity') return;
    this.finishCloseAnimation();
  }

  finishCloseAnimation() {
    if (!this.pendingClose) return;

    this.pendingClose = false;
    window.clearTimeout(this.closeFallbackTimeout);
    this.closeFallbackTimeout = null;
    if (this.menuContent) {
      this.menuContent.removeEventListener('transitionend', this.onCloseTransitionEnd);
    }

    super.close();
    this.menuContent?.classList.remove('is-menu-closing');
    HeaderMenu.updateBodyScrollLock();
  }

  onHoverMediaChange(mediaQuery) {
    if (mediaQuery.matches) {
      this.bindHoverEvents();
    } else {
      this.unbindHoverEvents();
      HeaderMenu.updateBodyScrollLock();
    }
  }

  bindHoverEvents() {
    if (this._hoverBound || !this.mainDetailsToggle || !this.summary) return;
    this._hoverBound = true;

    this.closeTimeout = null;
    this.openTimeout = null;
    this._hoverActive = false;
    this.menuContent =
      this.mainDetailsToggle.querySelector(
        ':scope > .super-mega-menu__content, :scope > .grid-mega-menu__content, :scope > .banner-mega-menu__content, :scope > .mega-menu__content, :scope > .header__submenu'
      ) || this.content;

    this.isDropdownMenu =
      !!this.menuContent?.classList.contains('header__submenu') &&
      this.menuContent.parentElement === this.mainDetailsToggle &&
      !this.mainDetailsToggle.classList.contains('super-mega-menu') &&
      !this.mainDetailsToggle.classList.contains('grid-mega-menu') &&
      !this.mainDetailsToggle.classList.contains('banner-mega-menu') &&
      !this.mainDetailsToggle.classList.contains('mega-menu');

    this._onSummaryClick = (event) => {
      const anchor = event.target.closest('a[href]');
      const href = anchor && this.summary.contains(anchor) ? anchor.getAttribute('href') : '';
      const canNavigate = Boolean(href && href !== '#' && href !== '/#');

      if (
        canNavigate &&
        (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0)
      ) {
        return;
      }

      event.preventDefault();
      if (canNavigate) window.location.assign(anchor.href);
    };
    this._onMouseEnter = () => {
      if (this.pendingClose) return;
      if (!this.isDropdownMenu && this.menuContent?.classList.contains('is-menu-closing')) return;

      this._hoverActive = true;
      window.clearTimeout(this.closeTimeout);
      window.clearTimeout(this.openTimeout);

      const hasOpenSibling = Array.from(document.querySelectorAll('header-menu')).some(
        (menu) => menu !== this && menu.mainDetailsToggle?.hasAttribute('open')
      );

      HeaderMenu.closeSiblingMenus(this);

      const openCurrent = () => {
        if (!this._hoverActive) return;
        this.open();
        this.onToggle();
      };

      if (hasOpenSibling) {
        this.openTimeout = window.setTimeout(openCurrent, HeaderMenu.MENU_SWITCH_MS);
      } else {
        openCurrent();
      }
    };
    this._onMouseLeave = () => {
      this._hoverActive = false;
      window.clearTimeout(this.openTimeout);
      this.openTimeout = null;
      window.clearTimeout(this.closeTimeout);
      this.closeTimeout = window.setTimeout(() => this.close(), 200);
    };
    this._onContentMouseEnter = () => {
      if (!this.isDropdownMenu && this.menuContent?.classList.contains('is-menu-closing')) return;

      window.clearTimeout(this.closeTimeout);
      if (!this.mainDetailsToggle.hasAttribute('open')) {
        this._onMouseEnter();
      }
    };

    this.summary.addEventListener('click', this._onSummaryClick);
    this.summary.addEventListener('mouseenter', this._onMouseEnter);
    this.addEventListener('mouseenter', this._onMouseEnter);
    this.addEventListener('mouseleave', this._onMouseLeave);

    if (this.menuContent) {
      this.menuContent.addEventListener('mouseenter', this._onContentMouseEnter);
      this.menuContent.addEventListener('mouseleave', this._onMouseLeave);
    }
  }

  unbindHoverEvents() {
    if (!this._hoverBound) return;
    this._hoverBound = false;

    window.clearTimeout(this.closeTimeout);
    window.clearTimeout(this.openTimeout);
    this.closeTimeout = null;
    this.openTimeout = null;
    if (this.summary) {
      this.summary.removeEventListener('click', this._onSummaryClick);
      this.summary.removeEventListener('mouseenter', this._onMouseEnter);
    }
    this.removeEventListener('mouseenter', this._onMouseEnter);
    this.removeEventListener('mouseleave', this._onMouseLeave);

    if (this.menuContent) {
      this.menuContent.removeEventListener('mouseenter', this._onContentMouseEnter);
      this.menuContent.removeEventListener('mouseleave', this._onMouseLeave);
    }
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);
